import DOMPurify from 'dompurify'

import chatTranslationCache from '@/modules/chat/logic/ChatTranslationCache'

// Normalize chat message language codes for comparison with user locale.
// Exceptions:
// - de_formal -> de
// - jp -> ja
// Returns lowercase code, or null for non-strings.
export function normalizeChatMessageLanguage(lang) {
  if (!lang || typeof lang !== 'string') {
    return null
  }

  const lower = lang.trim().toLowerCase()

  if (lower === 'de_formal') {
    return 'de'
  }
  if (lower === 'jp') {
    return 'ja'
  }

  return lower
}

// Sanitize HTML message content to plain text (preserving line breaks)
export function sanitizeToPlainText(html) {
  if (!html || typeof html !== 'string') {
    return ''
  }
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['span', 'br'],
    ALLOWED_ATTR: ['class'],
  })
  return sanitized.replace(/<br\s*\/??\s*>/gi, '\n')
}

// Extract a plain text representation for a message
function extractTextFromMessage(msg) {
  if (!msg) {
    return ''
  }
  const isAttachmentMsg = !!msg.metadata?.file_id
  if (isAttachmentMsg) {
    if (msg.metadata?.transcript) {
      return String(msg.metadata.transcript || '').trim()
    }
    return ''
  }
  return sanitizeToPlainText(msg.content || '').trim()
}

// Return previous textual messages that were sent immediately before startMessageId
async function getPreviousTextMessages(chatThreadStore, startMessageId) {
  const maxMessages = 3

  function fetchPreviousMessages() {
    const collectedPreviousMessages = []
    const combinedMessages = chatThreadStore.combinedMessages
    const startIdx = combinedMessages.findIndex((currentMessage) => currentMessage.id === startMessageId)
    if (startIdx === -1) {
      return false
    }
    for (let i = startIdx - 1; i >= 0 && collectedPreviousMessages.length < maxMessages; i--) {
      const prev = combinedMessages[i]
      const text = extractTextFromMessage(prev)
      if (text) {
        collectedPreviousMessages.push(prev)
      }
    }
    return collectedPreviousMessages
  }

  let collectedPreviousMessages = fetchPreviousMessages()
  let attempts = 0
  while (collectedPreviousMessages.length < maxMessages && !chatThreadStore.allMessagesAreLoaded && attempts < 3) {
    const messagesToFetch = Math.max(1, maxMessages - collectedPreviousMessages.length)
    // eslint-disable-next-line no-await-in-loop
    await chatThreadStore.fetchOlderMessagesIntoCache(messagesToFetch)
    attempts += 1
    collectedPreviousMessages = fetchPreviousMessages()
  }

  // We collected from bottom up; reverse to oldest -> newest
  collectedPreviousMessages.reverse()
  return collectedPreviousMessages
}

// Build translation context from up to 3 previous textual messages in this thread.
// Returns a string with lines like:
// A: {3rd previous}
// A: {2nd previous}
// B: {previous}
// A:
// This is then used as context, the actual message to translate is sent as the "text to translate"
async function buildTranslationContext(chatThreadStore, currentMessage) {
  const targetSenderId = currentMessage?.senderDisplayName
  const collectedPreviousMessages = await getPreviousTextMessages(chatThreadStore, currentMessage.id)

  // Format lines with A/B labels depending on author relative to target message author
  const lines = []
  for (const msg of collectedPreviousMessages) {
    const label = msg?.senderDisplayName === targetSenderId ? 'A' : 'B'
    const text = extractTextFromMessage(msg)
    lines.push(`${label}: ${text}`)
  }
  // Append final A: line to indicate the current message to translate
  lines.push('A:')

  return lines.join('\n')
}

// Fetch a translation for a message, including a short preceding context built from the thread.
// Returns the translated plain text string or null.
export async function translateMessageForDisplay({
  chatThreadStore,
  message,
  targetLanguage,
}) {
  const input = extractTextFromMessage(message)
  if (!input) {
    return null
  }

  const contextForApi = await buildTranslationContext(chatThreadStore, message)

  const result = await chatTranslationCache.translate({
    targetLanguage,
    text: input,
    context: contextForApi,
  })

  return result && result.translation ? result.translation : null
}
